<template>
  <transition name="fade-slide">
    <div
      v-if="visible"
      class="vol-pay-overlay"
      @click.self="dismissVolPay"
    >
      <div
        class="vol-pay-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vol-pay-title"
      >
        <button
          v-if="step !== 'success'"
          type="button"
          class="vol-pay-close"
          aria-label="关闭"
          @click="dismissVolPay"
        >
          <AppIcon name="close" size="sm" aria-label="" />
        </button>

        <div v-if="loading && !preview && step !== 'success'" class="vol-pay-loading">加载中...</div>

        <template v-else-if="step === 'success'">
          <h2 id="vol-pay-title" class="vol-pay-title">购买成功</h2>
          <p class="vol-pay-success-hint">已解锁，可以开始阅读</p>
          <button type="button" class="vol-pay-submit vol-pay-submit--block" @click="confirmVolPaySuccess">
            开始阅读
          </button>
        </template>

        <template v-else-if="preview && step === 'home'">
          <h2 id="vol-pay-title" class="vol-pay-title">当前卷为付费卷</h2>
          <div class="vol-pay-method" aria-label="支付方式">
            <AppIcon name="orangeCoin" size="md" />
            <span>橘子币支付</span>
          </div>
          <button
            v-if="showBulkEntry"
            type="button"
            class="vol-pay-bulk-entry"
            :disabled="loading"
            @click="goToBulkStep"
          >
            批量购买
            <AppIcon name="chevronRight" size="sm" />
          </button>
          <VolPayNoticeFold
            :balance-gold="preview.balanceGold"
            :agreement-title="agreementTitle"
            :open-agreement="openVolPayAgreement"
          />
        </template>

        <template v-else-if="preview && step === 'bulk'">
          <div class="vol-pay-bulk-head">
            <button type="button" class="vol-pay-back" aria-label="返回" @click="goToHomeStep">
              <AppIcon name="arrowLeft" size="sm" />
            </button>
            <div class="vol-pay-bulk-titles">
              <h2 id="vol-pay-title" class="vol-pay-title">当前为 第 {{ preview.volNum }} 卷</h2>
              <p class="vol-pay-bulk-sub">购买包含本卷</p>
            </div>
          </div>
          <div class="vol-pay-bulk" role="radiogroup" aria-label="批量购买档位">
            <button
              v-for="opt in availableBulkOptions"
              :key="opt.bulkType"
              type="button"
              class="vol-pay-bulk-opt"
              :class="{ 'is-selected': selectedBulkType === opt.bulkType }"
              :disabled="loading"
              @click="selectBulkType(opt.bulkType)"
            >
              {{ opt.label }}
            </button>
          </div>
          <VolPayNoticeFold
            :balance-gold="preview.balanceGold"
            :agreement-title="agreementTitle"
            :open-agreement="openVolPayAgreement"
          />
        </template>

        <p v-else-if="errorMessage" class="vol-pay-error">{{ errorMessage }}</p>

        <template v-if="preview && step !== 'success'">
          <p v-if="errorMessage" class="vol-pay-error">{{ errorMessage }}</p>
          <div class="vol-pay-footer">
            <div class="vol-pay-footer-price">
              <span class="vol-pay-footer-gold">{{ preview.totalGold }}</span>
              <span class="vol-pay-footer-unit">橘子币</span>
            </div>
            <button
              type="button"
              class="vol-pay-submit"
              :disabled="loading"
              @click="submitVolPay"
            >
              {{ loading ? '处理中...' : actionText }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import AppIcon from '@/shared/components/AppIcon.vue'
import { requestOpenAgreement } from '@/shared/utils/payDialogBridge'
import { useVolPay, closeVolPay } from '@/features/comic/composables/useVolPay'
import VolPayNoticeFold from './VolPayNoticeFold.vue'

const route = useRoute()

const {
  visible,
  loading,
  errorMessage,
  preview,
  selectedBulkType,
  step,
  availableBulkOptions,
  actionText,
  showBulkEntry,
  agreementTitle,
  agreementCode,
  selectBulkType,
  goToBulkStep,
  goToHomeStep,
  confirmVolPaySuccess,
  submitVolPay,
} = useVolPay()

/**
 * 打开购卷协议弹窗（不离开当前购卷窗）
 */
function openVolPayAgreement() {
  requestOpenAgreement(agreementCode.value)
}

/**
 * 用户关闭购卷窗（余额不足充值后仍不够等场景可退出）
 */
function dismissVolPay() {
  if (step.value === 'success' || loading.value) return
  closeVolPay()
}

/**
 * Esc 关闭购卷窗（购买成功页须点「开始阅读」）
 * @param {KeyboardEvent} event
 */
function onEscape(event) {
  if (event.key !== 'Escape' || !visible.value) return
  if (step.value === 'success' || loading.value) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  event.preventDefault()
  event.stopPropagation()
  dismissVolPay()
}

watch(
  visible,
  (isOpen) => {
    if (isOpen) {
      window.addEventListener('keydown', onEscape, true)
    } else {
      window.removeEventListener('keydown', onEscape, true)
    }
  },
  { immediate: true }
)

/** 离开当前路由时关掉全局购卷窗，避免跟到其他页面 */
watch(
  () => route.fullPath,
  (next, prev) => {
    if (prev != null && next !== prev && visible.value) {
      closeVolPay()
    }
  }
)

onUnmounted(() => {
  window.removeEventListener('keydown', onEscape, true)
})
</script>

<style scoped src="@/features/comic/styles/VolPayDialog.css"></style>
